/**
 * "Add to calendar" for a confirmed activity booking (TRAV23 activities-front merge).
 *
 * The activities front used `add-to-calendar-button-react`, which rendered a dropdown
 * of providers (Apple / Google / Yahoo / Microsoft 365 / Teams / Outlook.com). That
 * package is not a dependency here, and AGENTS.md forbids adding one without team
 * approval, so this is a dependency-free replacement: it builds an iCalendar file in
 * the browser and downloads it. Every one of those providers imports `.ics`, so the
 * capability is preserved, but it is one button rather than a provider picker.
 *
 * To restore the original dropdown, get `add-to-calendar-button-react` approved and
 * swap this component out.
 */

import React, { useCallback } from 'react';

import { Button } from 'antd';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

export interface IActivityAddToCalendarProps {
  name: string;
  location?: string;
  /** `YYYY-MM-DD`. */
  startDate: string;
  /** `YYYY-MM-DD`; defaults to `startDate`. */
  endDate?: string;
  /** `HH:mm`; when absent the event is all-day. */
  startTime?: string;
  /** `HH:mm`. */
  endTime?: string;
  description?: string;
  className?: string;
}

/** RFC 5545 escaping: backslash, semicolon, comma and newline are special in values. */
const escapeIcsText = (value: string): string =>
  value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');

/** Local wall-clock form (no trailing Z): the activity happens in its own timezone. */
const toIcsDateTime = (date: string, time?: string): string => {
  const parsed = time
    ? moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm')
    : moment(date, 'YYYY-MM-DD');

  return time ? parsed.format('YYYYMMDD[T]HHmmss') : parsed.format('YYYYMMDD');
};

export const ActivityAddToCalendar: React.FC<IActivityAddToCalendarProps> = ({
  name,
  location,
  startDate,
  endDate,
  startTime,
  endTime,
  description,
  className,
}) => {
  const handleDownload = useCallback(() => {
    if (!startDate) {
      return;
    }

    const isAllDay = !startTime;
    const dtStart = toIcsDateTime(startDate, startTime);
    const dtEnd = toIcsDateTime(endDate || startDate, endTime || startTime);
    const valueType = isAllDay ? ';VALUE=DATE' : '';

    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//RSI//Activities//EN',
      'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT',
      `UID:${Date.now()}@rsi-activities`,
      `DTSTAMP:${moment().utc().format('YYYYMMDD[T]HHmmss[Z]')}`,
      `DTSTART${valueType}:${dtStart}`,
      `DTEND${valueType}:${dtEnd}`,
      `SUMMARY:${escapeIcsText(name || '')}`,
      location ? `LOCATION:${escapeIcsText(location)}` : '',
      description ? `DESCRIPTION:${escapeIcsText(description)}` : '',
      'END:VEVENT',
      'END:VCALENDAR',
    ].filter(Boolean);

    // CRLF is required by RFC 5545; some clients reject LF-only files.
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${(name || 'activity').replace(/[^\w\- ]+/g, '').trim() || 'activity'}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [name, location, startDate, endDate, startTime, endTime, description]);

  if (!startDate) {
    return null;
  }

  return (
    <Button size="large" className={className} onClick={handleDownload}>
      <FormattedMessage id="activity.add.to.calendar" />
    </Button>
  );
};
